/*
 * kp-core-structure.js — the core structure band (§20).
 *
 * One custom element, three jobs: keep exactly one row open, crossfade the
 * media to match, and hydrate a row's video the first time it is opened.
 *
 * It is a tab set wearing an accordion, so aria-expanded is the contract and
 * arrow keys are deliberately NOT bound — §20 calls half a tablist worse than
 * none. Tab order is the row order; that is all the keyboard support this
 * pattern is supposed to have.
 *
 * The markup this enhances is already correct without it: the first row is
 * open, the rest are closed, every body is in the DOM. Nothing here creates
 * content — it only switches which one is showing.
 *
 * Nothing plays until the band is on screen, and nothing is fetched until it
 * is within one screen of it (#43, #46). Measured on WebKit iPhone: a play()
 * issued at page load, with the band 2700px below the fold, left the clip at
 * networkState IDLE for twenty seconds — no bytes moved until a second play()
 * with the well in view. So the "near" observer hydrates the open row's clip
 * with preload="metadata", the "visible" observer plays it and pauses the
 * band on the way out, and activate() only ever asks reel() to reconcile.
 *
 * A refused play() marks the slide `is-blocked` (#44): iOS Low Power Mode
 * turns scripted autoplay down, and this band has no other control. A tap on
 * the well then plays inside the gesture; a swipe still changes the row.
 *
 * Under prefers-reduced-motion the videos never load at all. A muted loop
 * with no controls is exactly the ambient motion the setting asks us to
 * stop, and there is no pause affordance to fall back on — so the row's
 * image, which doubles as the video's poster, simply stands. A row with no
 * image falls back to its empty-well label, exactly as it does before any
 * video would have loaded.
 *
 * Wrapped in an IIFE: every kp-*.js is a classic <script src>, and a section
 * with a preset can put the same file on a page twice (or share a page with
 * another kp file that names the same top-level const). A bare lexical
 * declaration then throws `already declared` and kills the whole file;
 * inside a function scope it is harmless, and customElements.get() keeps
 * the define idempotent. (Ticket #22.)
 */
(() => {
const KP_REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
// Below --kp-bp-stack the rows become a rail and one panel (§20 mobile).
const KP_STACKED = window.matchMedia('(max-width: 749px)');
// A horizontal drag on the band longer than this, and flatter than 45°,
// is a swipe to the next or previous system.
const KP_SWIPE_MIN = 40;

class KpCoreStructure extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-kp-core-row]'));
    this.slides = Array.from(this.querySelectorAll('[data-kp-core-slide]'));
    this.rail = this.querySelector('[data-kp-core-rail]');
    this.chips = this.rail ? Array.from(this.rail.querySelectorAll('[data-kp-core-chip]')) : [];
    if (!this.rows.length) return;

    this.addEventListener('click', this.onClick);

    // Swiping the film moves to the next system. Pointer events, so one
    // handler covers touch and a trackpad drag; touch-action leaves the
    // vertical scroll with the browser and only claims the horizontal.
    this.media = this.querySelector('.kp-core__media');
    if (this.media) {
      this.media.style.touchAction = 'pan-y';
      this.media.addEventListener('pointerdown', this.onPointerDown);
      this.media.addEventListener('pointerup', this.onPointerUp);
      this.media.addEventListener('pointercancel', this.onPointerUp);
    }

    // Selecting a block in the theme editor opens its row, so a merchant
    // editing row 04 is looking at row 04. Bound on the section, because
    // shopify:block:select fires on the section element, not on the block.
    this.section = this.closest('.shopify-section') || this;
    this.section.addEventListener('shopify:block:select', this.onBlockSelect);

    // The server-rendered open row is the starting truth, not index 0: a
    // merchant could reorder blocks and the template still marks one open.
    const open = this.rows.findIndex((row) => row.classList.contains('is-open'));
    this.activate(open === -1 ? 0 : open, { silent: true });

    this.watch();

    this.measure();
    // Orbitron and Rajdhani can land after first paint and rewrap the copy.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.measure);
    window.addEventListener('resize', this.onResize);
    KP_STACKED.addEventListener('change', this.measure);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('resize', this.onResize);
    KP_STACKED.removeEventListener('change', this.measure);
    window.clearTimeout(this.resizeTimer);
    if (this.observer) this.observer.disconnect();
    if (this.nearObserver) this.nearObserver.disconnect();
    if (this.media) {
      this.media.removeEventListener('pointerdown', this.onPointerDown);
      this.media.removeEventListener('pointerup', this.onPointerUp);
      this.media.removeEventListener('pointercancel', this.onPointerUp);
    }
    if (this.section) this.section.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  onBlockSelect = (event) => {
    const row = event.target.closest('[data-kp-core-row]');
    if (!row || !this.contains(row)) return;
    this.activate(Number(row.dataset.index));
  };

  onResize = () => {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(this.measure, 150);
  };

  /*
   * Phone: pins the panel column at the height of its tallest row, so
   * switching to a shorter one leaves the page the same length (see the
   * CSS on .kp-core__acc). Each row is opened in turn, unanimated, and the
   * column read; the open row is put back before anything paints.
   * Measured, not guessed: the copy is merchant-set and rewraps at every
   * width. Above --kp-bp-stack the pin is removed — the rows float over
   * the band there and have no column to pin.
   */
  measure = () => {
    const acc = this.querySelector('.kp-core__acc');
    if (!acc) return;

    this.style.removeProperty('--kp-core-panel-min');
    if (!KP_STACKED.matches) return;

    this.settle(acc);
    const open = this.rows.findIndex((row) => row.classList.contains('is-open'));

    let tallest = 0;
    this.rows.forEach((_, i) => {
      this.rows.forEach((row, j) => {
        row.classList.toggle('is-open', j === i);
        const body = row.querySelector('[data-kp-core-body]');
        if (body) {
          this.settle(body);
          body.hidden = j !== i;
        }
      });
      tallest = Math.max(tallest, acc.offsetHeight);
    });

    this.rows.forEach((row, j) => {
      row.classList.toggle('is-open', j === open);
      const body = row.querySelector('[data-kp-core-body]');
      if (body) body.hidden = j !== open;
    });

    if (tallest) this.style.setProperty('--kp-core-panel-min', `${Math.ceil(tallest)}px`);
  };

  // At every width, not just the phone: the swipe below is phone-only, but
  // the tap on a refused film is not.
  onPointerDown = (event) => {
    if (!event.isPrimary) return;
    this.swipe = { x: event.clientX, y: event.clientY };
  };

  onPointerUp = (event) => {
    const start = this.swipe;
    this.swipe = null;
    if (!start || event.type === 'pointercancel') return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // A tap, not a swipe, on a slide whose film was refused: play it now,
    // inside the gesture the browser was waiting for.
    if (Math.hypot(dx, dy) <= 10) {
      const blocked = event.target.closest('.is-blocked');
      if (blocked && this.contains(blocked)) {
        this.hydrateVideo(blocked);
        const video = blocked.querySelector('video');
        if (video) {
          video.dataset.kpByHand = '1';
          this.play(video);
        }
      }
      return;
    }

    if (!KP_STACKED.matches) return;
    if (Math.abs(dx) < KP_SWIPE_MIN || Math.abs(dx) < Math.abs(dy)) return;

    const open = this.rows.findIndex((row) => row.classList.contains('is-open'));
    const next = (open + (dx < 0 ? 1 : -1) + this.rows.length) % this.rows.length;
    this.activate(next);
  };

  onClick = (event) => {
    // A rail chip or a panel's "next" step — both name a row by index.
    const chip = event.target.closest('[data-kp-core-chip]');
    if (chip && this.contains(chip)) {
      const index = Number(chip.dataset.index);
      if (!this.rows[index] || this.rows[index].classList.contains('is-open')) return;
      this.activate(index);
      return;
    }

    const toggle = event.target.closest('[data-kp-core-toggle]');
    if (!toggle || !this.contains(toggle)) return;

    const index = Number(toggle.dataset.index);
    // Clicking the open row does nothing. There is no "all closed" state —
    // the band would show media with no label and no way back.
    if (this.rows[index] && this.rows[index].classList.contains('is-open')) return;

    this.activate(index);
  };

  activate(index, { silent = false } = {}) {
    // On the phone the closing row is display: none the moment it loses
    // is-open and the opening one appears at full size; the bodies are
    // swapped instantly and the ONE thing that animates is the panel
    // column's height, old size to new (settlePanel). Growing the new body
    // from zero after the old one had vanished was a collapse and then a
    // push — the layout shift under the thumb this replaces.
    const stacked = KP_STACKED.matches;
    const acc = this.querySelector('.kp-core__acc');
    const from = stacked && !silent && acc ? acc.offsetHeight : 0;

    this.rows.forEach((row, i) => {
      const isOpen = i === index;
      row.classList.toggle('is-open', isOpen);

      const toggle = row.querySelector('[data-kp-core-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));

      const body = row.querySelector('[data-kp-core-body]');
      if (body) this.reveal(body, isOpen, !silent && !stacked);
    });

    if (stacked && !silent && acc) this.settlePanel(acc, from);

    this.chips.forEach((chip) => {
      const isCurrent = Number(chip.dataset.index) === index;
      chip.classList.toggle('is-current', isCurrent);
      if (isCurrent) chip.setAttribute('aria-current', 'true');
      else chip.removeAttribute('aria-current');
    });

    // Keep the current chip on screen. Scrolled on the rail itself, never
    // scrollIntoView: that would also scroll the page to the rail.
    const chip = this.chips.find((c) => Number(c.dataset.index) === index);
    if (chip && this.rail && stacked) {
      const left = chip.offsetLeft - (this.rail.clientWidth - chip.offsetWidth) / 2;
      this.rail.scrollTo({ left, behavior: silent || KP_REDUCED_MOTION.matches ? 'auto' : 'smooth' });
    }

    this.slides.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
    });

    this.index = index;
    this.reel();
  }

  /*
   * Two observers, like the performance bands. "Near" is one viewport
   * ahead and only hydrates; "visible" is what plays. Without one, WebKit
   * on iPhone never began loading a clip that was played while off screen.
   */
  watch() {
    if (!('IntersectionObserver' in window)) {
      this.visible = true;
      this.near = true;
      this.reel();
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.visible = entry.isIntersecting;
        this.reel();
      });
    });
    this.observer.observe(this);

    this.nearObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.near = entry.isIntersecting;
          this.reel();
        });
      },
      { rootMargin: '100% 0px' }
    );
    this.nearObserver.observe(this);
  }

  /*
   * Brings the clips in line with what is open and what is on screen. One
   * method, so there is no order of events that leaves the open row's film
   * hydrated, visible and paused. Pausing the clips that are no longer
   * showing matters: an invisible autoplaying video still decodes every
   * frame, and five of them is the weight problem the deferred <template>
   * exists to avoid.
   */
  reel() {
    const videos = Array.from(this.querySelectorAll('video'));

    if (!this.visible && !this.near) {
      videos.forEach((video) => video.pause());
      return;
    }

    // Reduced motion requests nothing on its own; every slide still holding
    // a clip is marked so the tap is that visitor's way in, and a film they
    // started by hand runs while the band is on screen.
    if (KP_REDUCED_MOTION.matches) {
      videos.forEach((video) => {
        if (!(video.dataset.kpByHand && this.visible)) video.pause();
      });
      this.slides.forEach((slide) => {
        if (slide.querySelector('[data-kp-core-video]')) slide.classList.add('is-blocked');
      });
      return;
    }

    const active = this.slides[this.index];
    if (active) this.hydrateVideo(active);

    this.querySelectorAll('video').forEach((video) => {
      const on = this.visible && active && active.contains(video);
      if (on) this.play(video);
      else video.pause();
    });
  }

  play(video) {
    const played = video.play();
    if (!played || typeof played.catch !== 'function') return;
    played.catch((error) => {
      if (error && error.name === 'AbortError') return;
      const slide = video.closest('[data-kp-core-slide]');
      if (slide) slide.classList.add('is-blocked');
    });
  }

  // Phone: animates the panel column from the height it had before the
  // switch to the height it has now. Same bracket as reveal(): inline
  // height only while moving, cleared on transitionend, and never under
  // reduced motion, where a killed transition would strand it.
  settlePanel(acc, from) {
    this.settle(acc);
    if (KP_REDUCED_MOTION.matches) return;

    const to = acc.offsetHeight;
    if (!from || from === to) return;

    acc.style.height = `${from}px`;
    void acc.offsetHeight;
    acc.style.height = `${to}px`;

    acc.kpEnd = (event) => {
      if (event.target !== acc || event.propertyName !== 'height') return;
      this.settle(acc);
    };
    acc.addEventListener('transitionend', acc.kpEnd);
  }

  /*
   * Opens or closes one row body, growing it between zero and its own
   * measured height. Same recipe as kp-pdp-tech.js: `hidden` stays the
   * contract for the markup, screen readers and the no-JS page, and the
   * animation brackets it — off before an open, back on after a close.
   *
   * Measured at the moment of the toggle, not cached: the copy is
   * merchant-set and rewraps at every width.
   */
  reveal(body, open, animate) {
    this.settle(body);

    // A transition killed by prefers-reduced-motion never fires
    // transitionend, so the animated path would strand an inline height.
    if (!animate || KP_REDUCED_MOTION.matches) {
      body.hidden = !open;
      return;
    }

    const from = body.hidden ? 0 : body.scrollHeight;
    if (open) body.hidden = false;
    const to = open ? body.scrollHeight : 0;

    if (from === to) {
      body.hidden = !open;
      return;
    }

    body.style.height = `${from}px`;
    // Read it back so the browser has a start value to animate away from.
    void body.offsetHeight;
    body.style.height = `${to}px`;

    body.kpEnd = (event) => {
      // The bullets inside carry their own transitions; only this box's own
      // height ends the reveal.
      if (event.target !== body || event.propertyName !== 'height') return;
      this.settle(body);
      if (!open) body.hidden = true;
    };
    body.addEventListener('transitionend', body.kpEnd);
  }

  // Puts a body back on auto height and drops any listener still waiting on
  // it, so a toggle landing mid-animation replaces the old one instead of
  // racing it.
  settle(body) {
    if (body.kpEnd) {
      body.removeEventListener('transitionend', body.kpEnd);
      body.kpEnd = null;
    }
    body.style.height = '';
  }

  // Moves the <video> out of its <template> the first time its row opens, so
  // a clip nobody looks at is never requested. Runs once per slide: the
  // templates are removed with it.
  //
  // A slide can carry two templates, landscape and portrait. Which one
  // becomes real is decided here, once, from the viewport at first open,
  // and the other is dropped: swapping on rotate would refetch a clip
  // mid-read, and <source media> is not re-evaluated either. A slide with
  // only one template plays it at every width.
  hydrateVideo(slide) {
    const templates = Array.from(slide.querySelectorAll('[data-kp-core-video]'));
    if (!templates.length) return;

    const wanted = window.matchMedia('(max-width: 749px)').matches ? 'portrait' : 'landscape';
    const pick =
      templates.find((t) => (t.dataset.kpCoreVideo || 'landscape') === wanted) || templates[0];

    templates.forEach((t) => {
      if (t === pick) slide.appendChild(t.content.cloneNode(true));
      t.remove();
    });

    const video = slide.querySelector('video');
    if (!video) return;

    // A cloned node can reach the document with muted unset, and an unmuted
    // autoplay is refused. kp-video ships preload="none" so the <template>
    // cost nothing; real, the header may come in — reel() plays the rest.
    video.muted = true;
    video.preload = 'metadata';
    video.addEventListener('playing', () => slide.classList.remove('is-blocked'));
  }
}

if (!customElements.get('kp-core-structure')) {
  customElements.define('kp-core-structure', KpCoreStructure);
}
})();
